/**
 * BookInfographicBody — shared render body used by both the standalone
 * /infographic/:bookId page and the in-page overlay opened from
 * Bodha / Sankalpa. Owns the fetch, loading, error and success states.
 *
 * Props:
 *   bookId        — required, string
 *   bookSnapshot  — optional {id,title,author,summary,insights,actionItems,coreTheme}
 *                   forwarded to the backend so a first-time visitor
 *                   auto-hydrates the DB row instead of hitting a 404.
 *   lang          — "en" | "hi"   (default "en")
 *   onRetry       — optional callback rendered under the friendly error
 *   backHref      — optional href for the "back" link under error state
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Sprout, Sun, Mountain, Compass, Target, Eye, BookOpen, Feather,
  Lightbulb, Leaf, Hourglass, Footprints, Map, Sparkles, Scroll,
} from "lucide-react";

import GoldRule from "./homev3/GoldRule";
import { T } from "./homev3/tokens";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/book/infographic`;

// Map the backend icon vocab strings → lucide-react components.
const ICON_MAP = {
  "seedling": Sprout, "sun": Sun, "mountain": Mountain, "compass": Compass,
  "target": Target, "eye": Eye, "book-open": BookOpen, "feather": Feather,
  "lightbulb": Lightbulb, "leaf": Leaf, "hourglass": Hourglass,
  "footprints": Footprints, "map": Map, "sparkles": Sparkles, "scroll": Scroll,
};

function SectionIcon({ name, size = 32 }) {
  const C = ICON_MAP[name] || Sparkles;
  return <C size={size} strokeWidth={1.2} color={T.GOLD} aria-hidden />;
}

function SectionCard({ section, testId }) {
  const bodyIsList = Array.isArray(section.body);
  return (
    <article className="bic-section" data-testid={testId}>
      <header className="bic-section-head">
        <SectionIcon name={section.icon} />
        <div>
          <p className="bic-section-kicker">{section.kicker}</p>
          <h3 className="bic-section-title">{section.title}</h3>
        </div>
      </header>
      {bodyIsList ? (
        <ol className="bic-section-list">
          {section.body.map((line, i) => (
            <li key={i}>{line}</li>
          ))}
        </ol>
      ) : (
        <p className="bic-section-body">{section.body}</p>
      )}
    </article>
  );
}

function LoadingSkeleton() {
  return (
    <div className="bic-skeleton" data-testid="bic-loading">
      <div className="bic-skel-hero" />
      <div className="bic-skel-cols">
        <div className="bic-skel-card" />
        <div className="bic-skel-card" />
        <div className="bic-skel-card" />
        <div className="bic-skel-card" />
      </div>
    </div>
  );
}

function InfographicView({ data }) {
  const { hero, sections, closer, book, lang } = data;
  const left  = sections.slice(0, 3);
  const right = sections.slice(3, 6);

  return (
    <article className="bic-infographic" data-testid="bic-infographic" lang={lang || "en"}>
      <header className="bic-hero">
        <p className="bic-hero-kicker" data-testid="bic-hero-kicker">{hero.kicker}</p>
        <h1 className="bic-hero-title" data-testid="bic-hero-title">{book.title}</h1>
        <p className="bic-hero-author">{book.author}</p>
        <div className="bic-hero-rule"><GoldRule /></div>
        <p className="bic-hero-tagline" data-testid="bic-hero-tagline">{hero.tagline}</p>
      </header>

      <div className="bic-columns" data-testid="bic-columns">
        <div className="bic-col bic-col-left">
          {left.map((s) => (
            <SectionCard key={s.id} section={s} testId={`bic-section-${s.id}`} />
          ))}
        </div>
        <div className="bic-col bic-col-right">
          {right.map((s) => (
            <SectionCard key={s.id} section={s} testId={`bic-section-${s.id}`} />
          ))}
        </div>
      </div>

      <footer className="bic-closer" data-testid="bic-closer">
        <div className="bic-closer-rule"><GoldRule /></div>
        <p className="bic-closer-quote">
          &ldquo;{closer.quote}&rdquo;
        </p>
        <p className="bic-closer-attribution">— {closer.attribution}</p>
      </footer>
    </article>
  );
}

// Friendly on-brand error card. Never surfaces raw backend text.
function FriendlyError({ code, backHref }) {
  const isHydration = code === "book_not_hydrated" || code === 404;
  const message = isHydration
    ? "This book hasn't been read yet on this deck. Open it once from the library, then return to weave its infographic."
    : "The infographic couldn't be woven right now. Please try again shortly — the parchment is still setting.";
  return (
    <div className="bic-error" data-testid="bic-error">
      <p className="bic-error-eyebrow">A GENTLE PAUSE</p>
      <p className="bic-error-body">{message}</p>
      {backHref && (
        <Link to={backHref} className="bic-error-link" data-testid="bic-error-back">
          ← Return
        </Link>
      )}
    </div>
  );
}

export default function BookInfographicBody({
  bookId,
  bookSnapshot = null,
  lang = "en",
  backHref = "/infographic",
}) {
  const [state, setState] = useState({ loading: false, error: null, data: null });

  useEffect(() => {
    if (!bookId) return;
    let alive = true;
    (async () => {
      setState({ loading: true, error: null, data: null });
      try {
        const body = { book_id: bookId, targetLanguage: lang };
        if (bookSnapshot) body.book = bookSnapshot;
        const r = await axios.post(API, body, { timeout: 90000 });
        if (alive) setState({ loading: false, error: null, data: r.data });
      } catch (e) {
        if (!alive) return;
        // Normalize error shape — backend now returns {reason, message}
        // on 404, but we never surface backend text directly.
        const status = e?.response?.status;
        const reason = e?.response?.data?.detail?.reason;
        const errCode = reason || status || "unknown";
        setState({ loading: false, error: errCode, data: null });
      }
    })();
    return () => { alive = false; };
  }, [bookId, lang, bookSnapshot]);

  if (!bookId) return null;
  if (state.loading) return <LoadingSkeleton />;
  if (state.error !== null) return <FriendlyError code={state.error} backHref={backHref} />;
  if (state.data) return <InfographicView data={state.data} />;
  return null;
}

// Named exports for callers that want the sub-parts directly.
export { InfographicView, LoadingSkeleton, FriendlyError };
